import {
  resolveSongPreviewFromLink,
  detectSongLinkPlatform,
  type SongPreviewResolved,
} from "./wedding/songRequest";
import {
  loadWeddingBySlug,
  formatWeddingScheduleLines,
  formatRsvpDeadlineForDisplay,
  getWeddingCountdownTargetMs,
  isRsvpDeadlinePassed,
  getNormalizedTimelineItems,
  formatTimelineItemTime,
  getVenueLatLng,
  getActiveMealOptionKeys,
  type MealOptionKey,
  type WeddingDoc,
} from "./wedding/loadWedding";
import { buildWeatherSectionHtml, hydrateWeatherIfPresent } from "./wedding/weather";
import {
  buildWeddingIcsString,
  calendarInviteIcsFilename,
  downloadTextFileAsBlob,
  inviteCalendarDownloadable,
} from "./wedding/weddingIcs";
import { signInAnonymously } from "firebase/auth";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
} from "firebase/firestore";
import { getFirebase } from "./firebase/config";
import { destroyVenueMap, mountVenueMapKitWhenVisible } from "./venueMapKit";
import {
  applyDocumentLocale,
  applyLandingDocumentLocale,
  applyLegalDocumentLocale,
  formatGuestCapacity,
  getIntlLocale,
  getLandingStrings,
  getMessages,
  guestNumberLabel,
  inviteDocumentTitle,
  resolveAppLocale,
} from "./i18n";
import type { Messages } from "./i18n/types";
import { renderLanding, renderLegalPage } from "./landing";

function mealOptionLabelFromMessages(key: MealOptionKey, m: Messages): string {
  switch (key) {
    case "vegetarian":
      return m.mealVegetarian;
    case "vegan":
      return m.mealVegan;
    case "glutenFree":
      return m.mealGlutenFree;
    case "nutAllergy":
      return m.mealNutAllergy;
    default:
      return key;
  }
}

function isReservedLandingSegment(seg: string): boolean {
  const s = seg.toLowerCase();
  return s === "privacy" || s === "privacy.html" || s === "terms" || s === "terms.html";
}

/** `/privacy`, `/terms` — SPA ile ana sayfa düzeni; davet slug’u değil. */
function getLegalPageFromPath(): "privacy" | "terms" | null {
  let rawPath = window.location.pathname;
  try {
    rawPath = decodeURIComponent(rawPath);
  } catch {
    /* pathname çoğu ortamda zaten çözülmüş */
  }
  const path = rawPath.replace(/\/+$/, "");
  const parts = path.split("/").filter(Boolean);
  if (parts.length !== 1) return null;
  const seg = parts[0].toLowerCase();
  if (seg === "privacy" || seg === "privacy.html") return "privacy";
  if (seg === "terms" || seg === "terms.html") return "terms";
  return null;
}

function getSlugFromPath(): string | null {
  let rawPath = window.location.pathname;
  try {
    rawPath = decodeURIComponent(rawPath);
  } catch {
    /* pathname çoğu ortamda zaten çözülmüş */
  }
  const path = rawPath.replace(/\/+$/, "");
  const parts = path.split("/").filter(Boolean);
  if (parts.length >= 2 && parts[0] === "invite") {
    const s = (parts[1] ?? "").trim();
    return s || null;
  }
  if (parts.length === 1) {
    const s = (parts[0] ?? "").trim();
    if (isReservedLandingSegment(s)) return null;
    return s || null;
  }
  const q = new URLSearchParams(window.location.search);
  const fromQuery = (q.get("invite") ?? q.get("slug") ?? "").trim();
  return fromQuery || null;
}

function el(html: string): HTMLElement {
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  const n = t.content.firstElementChild;
  if (!(n instanceof HTMLElement)) throw new Error("template");
  return n;
}

/** Davet içeriği buraya yazılır; footer (#app içinde) korunur. */
function getAppContentMount(root: HTMLElement): HTMLElement {
  const inner = root.querySelector(".wedy-app-inner");
  return inner instanceof HTMLElement ? inner : root;
}

/** Tam ekran arka plan (bulanık filtre yok — Safari bellek uyarısını tetiklemez). */
function setPageHeroBackground(root: HTMLElement, heroSrc: string): void {
  const has = heroSrc.trim().length > 0;
  root.classList.toggle("wedy-root--hero-bg", has);
  if (has) {
    root.style.setProperty("--wedy-hero-bg", `url(${JSON.stringify(heroSrc.trim())})`);
  } else {
    root.style.removeProperty("--wedy-hero-bg");
  }
}

function clearPageHeroBackground(root: HTMLElement): void {
  root.classList.remove("wedy-root--hero-bg");
  root.style.removeProperty("--wedy-hero-bg");
}

let countdownIntervalId: ReturnType<typeof setInterval> | null = null;

let inviteHeroLayoutObserver: ResizeObserver | null = null;
let inviteLayoutMql: MediaQueryList | null = null;
let inviteLayoutMqlHandler: (() => void) | null = null;

function teardownInviteHeroLayoutSync(): void {
  if (inviteHeroLayoutObserver) {
    inviteHeroLayoutObserver.disconnect();
    inviteHeroLayoutObserver = null;
  }
  if (inviteLayoutMql && inviteLayoutMqlHandler) {
    inviteLayoutMql.removeEventListener("change", inviteLayoutMqlHandler);
    inviteLayoutMql = null;
    inviteLayoutMqlHandler = null;
  }
}

/** Geniş ekranda sağ sütun yüksekliğini davet kartına eşitlemek için (harita kalan alanı doldurur / metin uzayınca küçülür). */
function attachInviteLayoutHeroHeight(main: HTMLElement): void {
  const layout = main.querySelector(".wedy-invite-layout");
  if (!(layout instanceof HTMLElement)) return;
  const card = layout.querySelector(".wedy-invite-card");
  if (!(card instanceof HTMLElement)) return;

  const apply = (): void => {
    if (!window.matchMedia("(min-width: 880px)").matches) {
      layout.style.removeProperty("--wedy-invite-hero-h");
      return;
    }
    const h = Math.round(card.getBoundingClientRect().height);
    if (h > 0) {
      layout.style.setProperty("--wedy-invite-hero-h", `${h}px`);
    }
  };

  teardownInviteHeroLayoutSync();
  apply();
  inviteHeroLayoutObserver = new ResizeObserver(() => {
    requestAnimationFrame(apply);
  });
  inviteHeroLayoutObserver.observe(card);

  const mql = window.matchMedia("(min-width: 880px)");
  const onBp = (): void => {
    requestAnimationFrame(apply);
  };
  mql.addEventListener("change", onBp);
  inviteLayoutMql = mql;
  inviteLayoutMqlHandler = onBp;
}

function stopCountdown(): void {
  if (countdownIntervalId != null) {
    clearInterval(countdownIntervalId);
    countdownIntervalId = null;
  }
}

function renderLoading(root: HTMLElement, msg: Messages): void {
  root.classList.remove("wedy-root--landing");
  stopCountdown();
  teardownInviteHeroLayoutSync();
  destroyVenueMap();
  clearPageHeroBackground(root);
  getAppContentMount(root).replaceChildren(
    el(`<main class="wedy-stack"><p class="wedy-subtext">${escapeHtml(msg.loading)}</p></main>`),
  );
}

function renderError(root: HTMLElement, message: string, msg: Messages): void {
  root.classList.remove("wedy-root--landing");
  stopCountdown();
  teardownInviteHeroLayoutSync();
  destroyVenueMap();
  clearPageHeroBackground(root);
  getAppContentMount(root).replaceChildren(
    el(`<main class="wedy-stack">
      <div class="wedy-card wedy-card--thin" style="padding:20px;">
        <p class="wedy-label wedy-label--semibold">${escapeHtml(msg.invitationNotFound)}</p>
        <p class="wedy-subtext" style="margin-top:8px;">${escapeHtml(message)}</p>
      </div>
    </main>`),
  );
}

/** iOS `HomeHeroInvitationCard`: görsel + materyal footer; sağda isteğe bağlı geri sayım. */
function inviteHeroCardHtml(
  wedding: WeddingDoc,
  heroSrc: string,
  scheduleLines: string[],
  msg: Messages,
): string {
  const media = heroSrc.trim()
    ? `<img src="${escapeHtml(heroSrc)}" alt="" decoding="async" fetchpriority="high" />`
    : `<div class="wedy-invite-card__placeholder" role="img" aria-label="${escapeHtml(msg.noPhotoAria)}">${escapeHtml(msg.noPhoto)}</div>`;
  const datesHtml = scheduleLines
    .map((line) => `<p class="wedy-invite-card__date">${escapeHtml(line)}</p>`)
    .join("");
  const targetMs = getWeddingCountdownTargetMs(wedding);
  const countdownAside =
    targetMs != null
      ? `<aside class="wedy-invite-card__countdown" data-wedy-countdown data-target-ms="${targetMs}" aria-live="polite">
          <div class="wedy-countdown__grid">
            <div class="wedy-countdown__cell">
              <span class="wedy-countdown__val" data-cd-days>0</span>
              <span class="wedy-countdown__unit" data-cd-days-unit>${escapeHtml(msg.countdownDays)}</span>
            </div>
            <div class="wedy-countdown__cell">
              <span class="wedy-countdown__val" data-cd-hours>0</span>
              <span class="wedy-countdown__unit" data-cd-hours-unit>${escapeHtml(msg.countdownHours)}</span>
            </div>
            <div class="wedy-countdown__cell">
              <span class="wedy-countdown__val" data-cd-mins>0</span>
              <span class="wedy-countdown__unit" data-cd-mins-unit>${escapeHtml(msg.countdownMinutes)}</span>
            </div>
          </div>
        </aside>`
      : "";
  const calendarBtn =
    inviteCalendarDownloadable(wedding)
      ? `<button type="button" class="wedy-invite-card__calendar-btn" id="wedy-calendar-download" aria-label="${escapeHtml(msg.addToCalendarAria)}">${WEDY_INVITE_CALENDAR_BTN_ICON}<span class="wedy-invite-card__calendar-label">${escapeHtml(msg.addToCalendar)}</span></button>`
      : "";
  const footerTrailing =
    calendarBtn || countdownAside
      ? `<div class="wedy-invite-card__footer-trailing">
    ${calendarBtn}
    ${countdownAside}
  </div>`
      : "";
  return `<article class="wedy-invite-card">
    <div class="wedy-invite-card__image-wrap">
      ${media}
      <div class="wedy-invite-card__scrim" aria-hidden="true"></div>
    </div>
    <div class="wedy-invite-card__bottom">
      <footer class="wedy-invite-card__footer">
        <p class="wedy-invite-card__names-hero playfair-display-names">${escapeHtml(wedding.brideName)} &amp; ${escapeHtml(wedding.groomName)}</p>
        <div class="wedy-invite-card__footer-inner">
          <div class="wedy-invite-card__footer-main">
            <h1 class="wedy-invite-card__title">${escapeHtml(wedding.eventTitle)}</h1>
            ${datesHtml}
          </div>
          ${footerTrailing}
        </div>
      </footer>
    </div>
  </article>`;
}

function splitCountdown(remainingMs: number): { d: number; h: number; m: number } {
  const sec = Math.max(0, Math.floor(remainingMs / 1000));
  return {
    d: Math.floor(sec / 86400),
    h: Math.floor((sec % 86400) / 3600),
    m: Math.floor((sec % 3600) / 60),
  };
}

function startCountdown(root: HTMLElement, msg: Messages): void {
  stopCountdown();
  const host = root.querySelector("[data-wedy-countdown]");
  if (!(host instanceof HTMLElement)) return;
  const raw = host.dataset.targetMs;
  const targetMs = raw != null ? Number(raw) : NaN;
  if (!Number.isFinite(targetMs)) return;

  const elDays = host.querySelector("[data-cd-days]");
  const elHours = host.querySelector("[data-cd-hours]");
  const elMins = host.querySelector("[data-cd-mins]");
  const grid = host.querySelector(".wedy-countdown__grid");
  const doneHtml = `<p class="wedy-countdown__done-msg">${escapeHtml(msg.enjoyCelebration)}</p>`;

  const tick = (): void => {
    const left = targetMs - Date.now();
    if (left <= 0) {
      stopCountdown();
      host.classList.add("wedy-countdown--done");
      if (grid instanceof HTMLElement) grid.innerHTML = doneHtml;
      return;
    }

    const { d, h, m } = splitCountdown(left);
    if (elDays instanceof HTMLElement) elDays.textContent = String(d);
    if (elHours instanceof HTMLElement) elHours.textContent = String(h);
    if (elMins instanceof HTMLElement) elMins.textContent = String(m);
  };

  tick();
  countdownIntervalId = setInterval(tick, 1000);
}

/** Apple Maps — yol tarifi; iOS/macOS ve maps.apple.com ile açılır. */
function appleMapsDirectionsUrl(lat: number, lng: number): string {
  const dest = `${lat},${lng}`;
  return `https://maps.apple.com/?daddr=${encodeURIComponent(dest)}&dirflg=d`;
}

function googleMapsDirectionsUrl(lat: number, lng: number): string {
  const dest = `${lat},${lng}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

/** Konum kartı — Apple / Google yol tarifi (ikon + metin). */
function mapDirectionsRowHtml(lat: number, lng: number, msg: Messages): string {
  const apple = escapeHtml(appleMapsDirectionsUrl(lat, lng));
  const google = escapeHtml(googleMapsDirectionsUrl(lat, lng));
  const appleIcon = `<svg class="wedy-directions-link__icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>`;
  const googleIcon = `<svg class="wedy-directions-link__icon-svg wedy-directions-link__icon-svg--google" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>`;
  return `<div class="wedy-directions-row">
    <a
      class="wedy-directions-link wedy-directions-link--half"
      href="${apple}"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="${escapeHtml(msg.openAppleMaps)}"
    ><span class="wedy-directions-link__icon" aria-hidden="true">${appleIcon}</span><span>${escapeHtml(msg.appleMaps)}</span></a>
    <a
      class="wedy-directions-link wedy-directions-link--half"
      href="${google}"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="${escapeHtml(msg.openGoogleMaps)}"
    ><span class="wedy-directions-link__icon" aria-hidden="true">${googleIcon}</span><span>${escapeHtml(msg.googleMaps)}</span></a>
  </div>`;
}

function rsvpCapacityBanner(guestLimit: number, msg: Messages): string {
  return `<p class="wedy-rsvp-capacity">${escapeHtml(formatGuestCapacity(msg, guestLimit))}</p>`;
}

function hostMealNoteBlock(hostNoteRaw: string): string {
  const t = hostNoteRaw.trim();
  if (!t) return "";
  return `<div class="wedy-host-meal-note"><p class="wedy-subtext" style="margin:0;white-space:pre-line;">${escapeHtml(t)}</p></div>`;
}

function rsvpMealSectionForGuest(
  guestIndex: number,
  mealKeys: readonly MealOptionKey[],
  msg: Messages,
): string {
  if (mealKeys.length === 0) return "";
  const checks = mealKeys
    .map(
      (k) =>
        `<label class="wedy-check-row"><input type="checkbox" id="wedy-g${guestIndex}-meal-${k}" name="guest_${guestIndex}_meal_${k}" data-wedy-guest="${guestIndex}" data-wedy-meal="${escapeHtml(k)}" /> ${escapeHtml(mealOptionLabelFromMessages(k, msg))}</label>`,
    )
    .join("");
  return `<div class="wedy-meal-block wedy-meal-block--guest">
    <span class="wedy-label" style="font-size:15px;display:block;margin-bottom:8px;">${escapeHtml(msg.mealPreferencesOptional)}</span>
    ${checks}
  </div>`;
}

function rsvpGuestSlotsHtml(
  guestLimit: number,
  mealKeys: readonly MealOptionKey[],
  hostMealNote: string,
  msg: Messages,
): string {
  const noteBlock =
    mealKeys.length > 0 || hostMealNote.trim().length > 0 ? hostMealNoteBlock(hostMealNote) : "";
  let html =
    rsvpCapacityBanner(guestLimit, msg) +
    noteBlock +
    `<p class="wedy-subtext" style="margin:0 0 18px;">${escapeHtml(msg.rsvpHelpText)}</p>` +
    `<div class="wedy-guest-slots">`;

  for (let i = 0; i < guestLimit; i++) {
    const label = guestNumberLabel(msg, i + 1);
    const ph = i === 0 ? msg.fullNamePlaceholder : msg.optionalNamePlaceholder;
    html += `<div class="wedy-guest-slot">
      <label class="wedy-label wedy-label--semibold" style="display:block;margin-bottom:8px;" for="wedy-guest-name-${i}">${escapeHtml(label)}</label>
      <input class="wedy-input" type="text" id="wedy-guest-name-${i}" name="guest_name_${i}" data-wedy-guest-name="${i}" maxlength="120" autocomplete="${i === 0 ? "name" : "off"}" placeholder="${escapeHtml(ph)}" />
      ${rsvpMealSectionForGuest(i, mealKeys, msg)}
    </div>`;
  }
  return html + `</div>`;
}

const WEDY_CARD_HEAD_ICON_LOCATION = `<svg class="wedy-card__head-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
const WEDY_CARD_HEAD_ICON_ABOUT = `<svg class="wedy-card__head-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>`;
const WEDY_CARD_HEAD_ICON_RSVP = `<svg class="wedy-card__head-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/></svg>`;
const WEDY_CARD_HEAD_ICON_TIMELINE = `<svg class="wedy-card__head-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v5h2v-5zm4-3h-2v8h2V7zm4 4h-2v4h2v-4z"/></svg>`;
const WEDY_CARD_HEAD_ICON_MUSIC = `<svg class="wedy-card__head-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>`;

/** Kapak yok / yüklenemedi — önizleme kutusunda müzik ikonu. */
const WEDY_SONG_THUMB_PLACEHOLDER_SVG = `<svg class="wedy-song-preview__thumb-placeholder-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>`;

const WEDY_SONG_MAX = 5;
/** Önizleme kartında başlık taşmasını önlemek için görünen metin uzunluğu (tam başlık `title` ile kalır). */
const WEDY_SONG_PREVIEW_TITLE_MAX_CHARS = 10;

function ellipsizeSongPreviewTitle(text: string): string {
  const t = text.trim();
  const chars = [...t];
  if (chars.length <= WEDY_SONG_PREVIEW_TITLE_MAX_CHARS) return t;
  return `${chars.slice(0, WEDY_SONG_PREVIEW_TITLE_MAX_CHARS).join("")}...`;
}

const WEDY_SONG_DROP_ICON_SVG = `<svg class="wedy-song-slot__drop-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>`;

function extractDroppedUrl(dt: DataTransfer): string {
  const u = dt.getData("text/uri-list");
  if (u) {
    const line = u
      .split("\n")
      .map((l) => l.trim())
      .find((l) => l && !l.startsWith("#"));
    if (line) return line;
  }
  return dt.getData("text/plain").trim();
}

function syncSongSlotChrome(slot: HTMLElement, hasContent: boolean): void {
  slot.setAttribute("data-wedy-empty", hasContent ? "false" : "true");
  const clearBtn = slot.querySelector("[data-wedy-song-clear]");
  if (clearBtn instanceof HTMLElement) {
    clearBtn.hidden = !hasContent;
  }
}

/** Davet kartı — geri sayım paneliyle aynı materyal çerçeve; yalnızca ikon. */
/** Material “calendar month” — üstte iki halka, gövde + ızgaraya yakın çizgiler. */
const WEDY_INVITE_CALENDAR_BTN_ICON = `<svg class="wedy-invite-card__calendar-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM7 2h2v2H7V2zm8 0h2v2h-2V2zM5 8V6h14v2H5z"/></svg>`;

function sectionCardHeadSimple(title: string, iconSvg: string): string {
  return `<header class="wedy-card__head">
    <span class="wedy-card__head-icon" aria-hidden="true">${iconSvg}</span>
    <p class="wedy-label wedy-label--semibold wedy-card-section-title">${escapeHtml(title)}</p>
  </header>`;
}

function rsvpFormCardHtml(
  wedding: WeddingDoc,
  guestLimit: number,
  mealKeys: readonly MealOptionKey[],
  hostMealNote: string,
  msg: Messages,
  intlLocale: string,
): string {
  const deadlineDate = formatRsvpDeadlineForDisplay(wedding, intlLocale);
  const deadlineChip =
    deadlineDate != null
      ? `<div class="wedy-rsvp-deadline-chip">
          <span class="wedy-rsvp-deadline-chip__label">${escapeHtml(msg.lastDay)}</span>
          <span class="wedy-rsvp-deadline-chip__date">${escapeHtml(deadlineDate)}</span>
        </div>`
      : "";
  return `<div class="wedy-card">
    <header class="wedy-card__head">
      <span class="wedy-card__head-icon" aria-hidden="true">${WEDY_CARD_HEAD_ICON_RSVP}</span>
      <div class="wedy-card__head-row">
        <p class="wedy-label wedy-label--semibold wedy-card-section-title">${escapeHtml(msg.rsvp)}</p>
        ${deadlineChip}
      </div>
    </header>
    <div class="wedy-card__body">
    <p id="wedy-rsvp-session-note" class="wedy-subtext" style="margin:0 0 14px;">${escapeHtml(msg.rsvpSessionNote)}</p>
    ${rsvpGuestSlotsHtml(guestLimit, mealKeys, hostMealNote, msg)}
    <div class="wedy-rsvp-actions" style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;margin-top:20px;">
      <button id="wedy-rsvp-submit" type="button" class="wedy-cta">${escapeHtml(msg.submitRsvp)}</button>
      <button type="button" class="wedy-cta wedy-cta--secondary" id="wedy-rsvp-withdraw" hidden>${escapeHtml(msg.rsvpWithdraw)}</button>
    </div>
    <p id="wedy-rsvp-msg" class="wedy-subtext" style="margin-top:12px;min-height:1.2em;"></p>
    <dialog id="wedy-rsvp-withdraw-dialog" class="wedy-rsvp-dialog">
      <h2 class="wedy-label wedy-label--semibold" style="margin:0 0 12px;font-size:17px;">${escapeHtml(msg.rsvpWithdrawDialogTitle)}</h2>
      <p class="wedy-subtext" style="margin:0 0 20px;white-space:pre-line;">${escapeHtml(msg.rsvpWithdrawDialogBody)}</p>
      <div style="display:flex;gap:12px;justify-content:flex-end;flex-wrap:wrap;">
        <button type="button" class="wedy-cta wedy-cta--secondary" id="wedy-rsvp-withdraw-cancel">${escapeHtml(msg.rsvpWithdrawCancel)}</button>
        <button type="button" class="wedy-cta" id="wedy-rsvp-withdraw-confirm" style="background:var(--wedy-red);color:#fff;border:none;">${escapeHtml(msg.rsvpWithdrawConfirm)}</button>
      </div>
    </dialog>
    </div>
  </div>`;
}

function guestsFromFirestoreRsvp(data: DocumentData): { name: string; mealPreferences: string[] }[] | null {
  const guests = data["guests"];
  if (!Array.isArray(guests) || guests.length === 0) return null;
  const out: { name: string; mealPreferences: string[] }[] = [];
  for (const raw of guests) {
    if (!raw || typeof raw !== "object") return null;
    const rec = raw as Record<string, unknown>;
    const name = typeof rec["name"] === "string" ? rec["name"].trim() : "";
    if (!name) return null;
    const mpRaw = rec["mealPreferences"];
    const mealPreferences =
      Array.isArray(mpRaw) && mpRaw.every((x) => typeof x === "string") ? (mpRaw as string[]) : [];
    out.push({ name, mealPreferences });
  }
  return out.length ? out : null;
}

function prefillRsvpForm(
  root: HTMLElement,
  guests: { name: string; mealPreferences: string[] }[],
  guestLimit: number,
): void {
  for (let i = 0; i < guestLimit; i++) {
    const input = root.querySelector(`input[data-wedy-guest-name="${i}"]`);
    const g = guests[i];
    if (input instanceof HTMLInputElement) {
      input.value = g ? g.name : "";
      input.disabled = false;
    }
    root.querySelectorAll(`input[type=checkbox][data-wedy-guest="${i}"][data-wedy-meal]`).forEach((node) => {
      if (!(node instanceof HTMLInputElement)) return;
      const key = node.getAttribute("data-wedy-meal") ?? "";
      node.checked = g ? g.mealPreferences.includes(key) : false;
      node.disabled = false;
    });
  }
}

function collectFilledGuests(
  root: HTMLElement,
  guestLimit: number,
  nameTooLongText: string,
): { name: string; mealPreferences: string[] }[] {
  const out: { name: string; mealPreferences: string[] }[] = [];
  for (let i = 0; i < guestLimit; i++) {
    const input = root.querySelector(`input[data-wedy-guest-name="${i}"]`);
    const name = input instanceof HTMLInputElement ? input.value.trim() : "";
    if (!name) continue;
    if (name.length > 120) {
      throw new Error(nameTooLongText);
    }
    const prefs: string[] = [];
    root.querySelectorAll(`input[type=checkbox][data-wedy-guest="${i}"][data-wedy-meal]`).forEach((node) => {
      if (node instanceof HTMLInputElement && node.checked) {
        const k = node.getAttribute("data-wedy-meal");
        if (k) prefs.push(k);
      }
    });
    out.push({ name, mealPreferences: prefs });
  }
  return out;
}

function songRequestSlotTileHtml(msg: Messages, slotIndex: number): string {
  const aria = escapeHtml(msg.songRequestSlotAria.replace("{n}", String(slotIndex)));
  const hint = escapeHtml(msg.songRequestSlotPasteHint);
  return `<div class="wedy-song-slot" data-wedy-song-slot data-wedy-empty="true">
    <div class="wedy-song-slot__tile">
      <button type="button" class="wedy-song-slot__clear" data-wedy-song-clear hidden aria-label="${escapeHtml(msg.songRequestRemoveTrack)}">×</button>
      <div class="wedy-song-slot__zone" data-wedy-song-zone>
        <span class="wedy-song-slot__badge" aria-hidden="true">${slotIndex}</span>
        ${WEDY_SONG_DROP_ICON_SVG}
        <span class="wedy-song-slot__hint wedy-subtext">${hint}</span>
        <input class="wedy-song-link-input" type="text" inputmode="url" autocomplete="off" autocorrect="off" spellcheck="false" aria-label="${aria}"/>
      </div>
      <div class="wedy-song-preview wedy-song-preview--compact" data-wedy-song-preview hidden>
        <div class="wedy-song-preview__thumb-wrap">
          <img class="wedy-song-preview__thumb" alt="" width="40" height="40" decoding="async"/>
          <span class="wedy-song-preview__thumb-placeholder" aria-hidden="true">${WEDY_SONG_THUMB_PLACEHOLDER_SVG}</span>
        </div>
        <div class="wedy-song-preview__meta">
          <p class="wedy-song-preview__title wedy-label wedy-label--semibold"></p>
          <span class="wedy-song-preview__badge" data-wedy-song-platform aria-hidden="true"></span>
        </div>
      </div>
      <p class="wedy-song-preview-status wedy-subtext" data-wedy-song-status style="margin:6px 0 0;min-height:1.1em;"></p>
    </div>
  </div>`;
}

function songRequestSlotsGridHtml(msg: Messages): string {
  let html = "";
  for (let i = 1; i <= WEDY_SONG_MAX; i++) {
    html += songRequestSlotTileHtml(msg, i);
  }
  return html;
}

function songRequestCardHtml(msg: Messages): string {
  return `<div class="wedy-card" data-wedy-song-request-root>
    <header class="wedy-card__head">
      <span class="wedy-card__head-icon" aria-hidden="true">${WEDY_CARD_HEAD_ICON_MUSIC}</span>
      <p class="wedy-label wedy-label--semibold wedy-card-section-title">${escapeHtml(msg.songRequestTitle)}</p>
    </header>
    <div class="wedy-card__body">
      <p class="wedy-subtext" style="margin:0 0 14px;">${escapeHtml(msg.songRequestIntro)}</p>
      <label class="wedy-label wedy-label--semibold" style="display:block;margin-bottom:8px;" for="wedy-song-requester">${escapeHtml(msg.songRequestRequesterLabel)}</label>
      <input class="wedy-input" type="text" id="wedy-song-requester" maxlength="120" autocomplete="name" placeholder="${escapeHtml(msg.songRequestRequesterPlaceholder)}"/>
      <p class="wedy-subtext" style="margin:14px 0 10px;">${escapeHtml(msg.songRequestSlotsCaption)}</p>
      <div class="wedy-song-slots" data-wedy-song-slots>
        ${songRequestSlotsGridHtml(msg)}
      </div>
      <button type="button" class="wedy-cta" id="wedy-song-submit" style="margin-top:20px;">${escapeHtml(msg.songRequestSubmit)}</button>
      <p id="wedy-song-msg" class="wedy-subtext" style="margin-top:12px;min-height:1.2em;"></p>
    </div>
  </div>`;
}

type SongSlotState = {
  wrap: HTMLElement;
  input: HTMLInputElement;
  preview: HTMLElement;
  status: HTMLElement | null;
  debounce: ReturnType<typeof setTimeout> | null;
  resolved: SongPreviewResolved | null;
};

/**
 * Slottan state üretir. `previous` verilirse aynı `wrap` düğümü için önceki state nesnesi
 * yeniden kullanılır — böylece `resolved` / `debounce` korunur ve async önizleme callback’i
 * hâlâ güncel dizideki nesneyi günceller (submit sonrası sıfırlanmaz).
 */
function collectSongSlotStates(slotsHost: HTMLElement, previous?: SongSlotState[]): SongSlotState[] {
  const prevByWrap = new Map<HTMLElement, SongSlotState>();
  for (const p of previous ?? []) {
    prevByWrap.set(p.wrap, p);
  }
  const out: SongSlotState[] = [];
  slotsHost.querySelectorAll("[data-wedy-song-slot]").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const input = node.querySelector(".wedy-song-link-input");
    if (!(input instanceof HTMLInputElement)) return;
    const preview = node.querySelector("[data-wedy-song-preview]");
    if (!(preview instanceof HTMLElement)) return;
    const status = node.querySelector("[data-wedy-song-status]");
    const st = status instanceof HTMLElement ? status : null;

    const existing = prevByWrap.get(node);
    if (existing) {
      existing.input = input;
      existing.preview = preview;
      existing.status = st;
      out.push(existing);
    } else {
      out.push({
        wrap: node,
        input,
        preview,
        status: st,
        debounce: null,
        resolved: null,
      });
    }
  });
  return out;
}

/** Slot boşaltılınca veya iptal edilince önizleme DOM’unu sıfırlar (geç yüzlenen img callback’leri için). */
function resetSongSlotPreviewVisuals(s: SongSlotState): void {
  s.resolved = null;
  s.preview.hidden = true;
  const wrap = s.preview.querySelector(".wedy-song-preview__thumb-wrap");
  const img = s.preview.querySelector(".wedy-song-preview__thumb");
  const titleEl = s.preview.querySelector(".wedy-song-preview__title");
  const badge = s.preview.querySelector("[data-wedy-song-platform]");
  if (wrap instanceof HTMLElement) wrap.classList.remove("wedy-song-preview__thumb-wrap--has-art");
  if (img instanceof HTMLImageElement) {
    img.hidden = true;
    img.onload = null;
    img.onerror = null;
    /* Önce src kaldır — boş src anında tarayıcı kırık ikon boyamasın */
    img.removeAttribute("src");
  }
  if (titleEl instanceof HTMLElement) {
    titleEl.textContent = "";
    titleEl.removeAttribute("title");
  }
  if (badge instanceof HTMLElement) {
    badge.textContent = "";
    badge.removeAttribute("aria-label");
  }
}

function setSongPreviewLoading(s: SongSlotState, msg: Messages): void {
  s.resolved = null;
  s.preview.hidden = true;
  if (s.status) s.status.textContent = msg.songRequestPreviewLoading;
}

function setSongPreviewError(s: SongSlotState, msg: Messages): void {
  s.resolved = null;
  s.preview.hidden = true;
  if (s.status) s.status.textContent = msg.songRequestPreviewError;
}

function applySongResolvedPreview(s: SongSlotState, data: SongPreviewResolved, msg: Messages): void {
  s.resolved = data;
  const wrap = s.preview.querySelector(".wedy-song-preview__thumb-wrap");
  const img = s.preview.querySelector(".wedy-song-preview__thumb");
  const titleEl = s.preview.querySelector(".wedy-song-preview__title");
  const badge = s.preview.querySelector("[data-wedy-song-platform]");
  if (wrap instanceof HTMLElement) {
    wrap.classList.remove("wedy-song-preview__thumb-wrap--has-art");
  }
  if (img instanceof HTMLImageElement) {
    img.onload = null;
    img.onerror = null;
    const url = (data.thumbnailUrl ?? "").trim();
    if (!url) {
      img.hidden = true;
      img.removeAttribute("src");
    } else {
      /* Görünürlük CSS ile: yalnızca başarılı yüklemede --has-art — yükleme sırasında boş kutu yok. */
      const w = wrap instanceof HTMLElement ? wrap : null;
      const setHasArt = (): void => {
        if (w) w.classList.add("wedy-song-preview__thumb-wrap--has-art");
      };
      const clearHasArt = (): void => {
        if (w) w.classList.remove("wedy-song-preview__thumb-wrap--has-art");
      };
      img.onload = () => setHasArt();
      img.onerror = () => {
        img.hidden = true;
        img.removeAttribute("src");
        clearHasArt();
      };
      /* Önce gizli + src ver; sonra görünür yap — boş src karesi / kırık ikon flaşı olmasın */
      img.hidden = true;
      img.src = url;
      img.removeAttribute("hidden");
      if (img.complete) {
        if (img.naturalWidth > 0) setHasArt();
        else {
          img.removeAttribute("src");
          img.hidden = true;
          clearHasArt();
        }
      }
    }
  }
  if (titleEl instanceof HTMLElement) {
    const full = data.trackTitle.trim();
    titleEl.textContent = ellipsizeSongPreviewTitle(full);
    titleEl.title =
      [...full].length > WEDY_SONG_PREVIEW_TITLE_MAX_CHARS ? full : "";
  }
  if (badge instanceof HTMLElement) {
    if (data.platform === "spotify") {
      badge.textContent = "Spotify";
      badge.setAttribute("aria-label", msg.songRequestSpotifyAria);
    } else {
      badge.textContent = "Apple Music";
      badge.setAttribute("aria-label", msg.songRequestAppleMusicAria);
    }
  }
  s.preview.hidden = false;
  if (s.status) s.status.textContent = "";
  syncSongSlotChrome(s.wrap, true);
}

function scheduleSongResolve(s: SongSlotState, msg: Messages): void {
  if (s.debounce != null) clearTimeout(s.debounce);
  const raw = s.input.value.trim();
  if (!raw) {
    resetSongSlotPreviewVisuals(s);
    if (s.status) s.status.textContent = "";
    syncSongSlotChrome(s.wrap, false);
    return;
  }
  syncSongSlotChrome(s.wrap, true);
  if (!detectSongLinkPlatform(raw)) {
    resetSongSlotPreviewVisuals(s);
    if (s.status) s.status.textContent = msg.songRequestInvalidLink;
    return;
  }
  setSongPreviewLoading(s, msg);
  s.debounce = setTimeout(() => {
    s.debounce = null;
    const requested = raw;
    void resolveSongPreviewFromLink(requested)
      .then((data) => {
        if (s.input.value.trim() !== requested) return;
        applySongResolvedPreview(s, data, msg);
      })
      .catch(() => {
        if (s.input.value.trim() !== requested) return;
        setSongPreviewError(s, msg);
      });
  }, 420);
}

function attachSongRequestForm(root: HTMLElement, slug: string, str: Messages): void {
  const slotsHost = root.querySelector("[data-wedy-song-slots]");
  const submitBtn = root.querySelector("#wedy-song-submit");
  const msgEl = root.querySelector("#wedy-song-msg");
  const requesterInput = root.querySelector("#wedy-song-requester");
  if (
    !(slotsHost instanceof HTMLElement) ||
    !(submitBtn instanceof HTMLButtonElement) ||
    !(msgEl instanceof HTMLElement) ||
    !(requesterInput instanceof HTMLInputElement)
  ) {
    return;
  }

  let states = collectSongSlotStates(slotsHost);
  states.forEach((s) => syncSongSlotChrome(s.wrap, s.input.value.trim().length > 0));

  const bindSlot = (s: SongSlotState): void => {
    const onChange = (): void => scheduleSongResolve(s, str);
    s.input.addEventListener("input", onChange);
    s.input.addEventListener("paste", () => window.setTimeout(onChange, 0));

    const zone = s.wrap.querySelector("[data-wedy-song-zone]");
    if (zone instanceof HTMLElement) {
      zone.addEventListener("dragover", (e) => {
        e.preventDefault();
        zone.classList.add("wedy-song-zone--dropping");
      });
      zone.addEventListener("dragleave", () => zone.classList.remove("wedy-song-zone--dropping"));
      zone.addEventListener("drop", (e) => {
        e.preventDefault();
        zone.classList.remove("wedy-song-zone--dropping");
        const dt = e.dataTransfer;
        if (!dt) return;
        const url = extractDroppedUrl(dt);
        if (!url) return;
        s.input.value = url;
        scheduleSongResolve(s, str);
        s.input.focus();
      });
    }
  };
  states.forEach(bindSlot);

  slotsHost.addEventListener("click", (ev) => {
    const t = ev.target;
    if (!(t instanceof HTMLElement)) return;
    if (!t.closest("[data-wedy-song-clear]")) return;
    const slot = t.closest("[data-wedy-song-slot]");
    if (!(slot instanceof HTMLElement)) return;
    const st = states.find((x) => x.wrap === slot);
    if (!st) return;
    st.input.value = "";
    scheduleSongResolve(st, str);
    msgEl.textContent = "";
  });

  submitBtn.addEventListener("click", async () => {
    msgEl.textContent = "";
    const name = requesterInput.value.trim();
    if (!name) {
      msgEl.textContent = str.songRequestNeedName;
      return;
    }
    if (name.length > 120) {
      msgEl.textContent = str.nameTooLong;
      return;
    }
    states = collectSongSlotStates(slotsHost, states);
    const toSend = states.map((s) => s.resolved).filter((x): x is SongPreviewResolved => x != null);
    if (toSend.length === 0) {
      msgEl.textContent = str.songRequestNeedOneResolvedTrack;
      return;
    }
    submitBtn.disabled = true;
    try {
      const { db } = getFirebase();
      for (const r of toSend) {
        const payload: DocumentData = {
          requesterName: name,
          trackUrl: r.trackUrl,
          platform: r.platform === "spotify" ? "spotify" : "appleMusic",
          trackTitle: r.trackTitle,
          artistName: r.artistName,
          thumbnailUrl: r.thumbnailUrl || "",
          createdAt: serverTimestamp(),
          submittedFrom: "inviteWeb",
        };
        await addDoc(collection(db, "weddings", slug, "songRequests"), payload);
      }
      msgEl.textContent = str.songRequestThankYou;
      requesterInput.disabled = true;
      submitBtn.disabled = true;
      states.forEach((s) => {
        s.input.disabled = true;
      });
      slotsHost.querySelectorAll("[data-wedy-song-clear]").forEach((x) => {
        if (x instanceof HTMLButtonElement) x.disabled = true;
      });
    } catch (e) {
      submitBtn.disabled = false;
      msgEl.textContent = e instanceof Error ? e.message : str.songRequestSubmitError;
    }
  });
}

function attachRsvpForm(root: HTMLElement, slug: string, guestLimit: number, str: Messages): void {
  void attachRsvpFormAsync(root, slug, guestLimit, str);
}

async function attachRsvpFormAsync(
  root: HTMLElement,
  slug: string,
  guestLimit: number,
  str: Messages,
): Promise<void> {
  const btn = root.querySelector("#wedy-rsvp-submit");
  const msg = root.querySelector("#wedy-rsvp-msg");
  const withdrawBtn = root.querySelector("#wedy-rsvp-withdraw");
  const dialog = root.querySelector("#wedy-rsvp-withdraw-dialog");
  const withdrawCancel = root.querySelector("#wedy-rsvp-withdraw-cancel");
  const withdrawConfirm = root.querySelector("#wedy-rsvp-withdraw-confirm");
  if (
    !(btn instanceof HTMLButtonElement) ||
    !(msg instanceof HTMLElement) ||
    !(withdrawBtn instanceof HTMLButtonElement) ||
    !(dialog instanceof HTMLDialogElement) ||
    !(withdrawCancel instanceof HTMLButtonElement) ||
    !(withdrawConfirm instanceof HTMLButtonElement)
  ) {
    return;
  }

  let hasSavedParty = false;

  const setWithdrawVisible = (show: boolean): void => {
    withdrawBtn.hidden = !show;
    if (!show) {
      withdrawBtn.setAttribute("aria-hidden", "true");
    } else {
      withdrawBtn.removeAttribute("aria-hidden");
    }
  };

  const setSubmitLabel = (): void => {
    btn.textContent = hasSavedParty ? str.rsvpSaveChanges : str.submitRsvp;
  };

  /* Yalnızca Firestore’da bu oturum için kayıtlı RSVP varken göster */
  setWithdrawVisible(false);

  btn.disabled = true;
  withdrawBtn.disabled = true;
  msg.textContent = str.loading;

  let uid: string;
  try {
    const { auth } = getFirebase();
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
    uid = auth.currentUser?.uid ?? "";
    if (!uid) throw new Error("no uid");
  } catch {
    msg.textContent = str.rsvpAuthError;
    btn.disabled = false;
    withdrawBtn.disabled = false;
    return;
  }

  const { db } = getFirebase();
  const rsvpRef = doc(db, "weddings", slug, "rsvps", uid);

  try {
    const snap = await getDoc(rsvpRef);
    if (snap.exists()) {
      const parsed = guestsFromFirestoreRsvp(snap.data());
      if (parsed && parsed.length > 0) {
        prefillRsvpForm(root, parsed, guestLimit);
        hasSavedParty = true;
        setWithdrawVisible(true);
      }
    }
  } catch (e) {
    msg.textContent = e instanceof Error ? e.message : str.rsvpSubmitError;
    btn.disabled = false;
    withdrawBtn.disabled = false;
    setSubmitLabel();
    return;
  }

  msg.textContent = "";
  btn.disabled = false;
  withdrawBtn.disabled = false;
  setSubmitLabel();

  const replaceUrlAfterSave = (): void => {
    try {
      const base = `${window.location.pathname}${window.location.search}`;
      history.replaceState(null, "", `${base}#rsvp`);
    } catch {
      /* ignore */
    }
  };

  btn.addEventListener("click", async () => {
    msg.textContent = "";
    let guestsPayload: { name: string; mealPreferences: string[] }[];
    try {
      guestsPayload = collectFilledGuests(root, guestLimit, str.nameTooLong);
    } catch (e) {
      msg.textContent = e instanceof Error ? e.message : str.invalidData;
      return;
    }
    if (guestsPayload.length === 0) {
      msg.textContent = str.enterGuestName;
      return;
    }

    btn.disabled = true;
    withdrawBtn.disabled = true;
    try {
      const party = guestsPayload.map((g) => ({
        name: g.name,
        mealPreferences: g.mealPreferences,
      }));
      const common = {
        guests: party,
        guestCount: guestsPayload.length,
        submittedFrom: "inviteWeb",
      };
      if (hasSavedParty) {
        await updateDoc(rsvpRef, {
          ...common,
          updatedAt: serverTimestamp(),
        });
        msg.textContent = str.rsvpThankYou;
      } else {
        await setDoc(rsvpRef, {
          ...common,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        hasSavedParty = true;
        setWithdrawVisible(true);
        setSubmitLabel();
        msg.textContent = str.rsvpThankYou;
      }
      replaceUrlAfterSave();
    } catch (e) {
      msg.textContent = e instanceof Error ? e.message : str.rsvpSubmitError;
    } finally {
      btn.disabled = false;
      withdrawBtn.disabled = false;
    }
  });

  withdrawBtn.addEventListener("click", () => {
    if (!hasSavedParty) return;
    dialog.showModal();
  });

  withdrawCancel.addEventListener("click", () => dialog.close());

  withdrawConfirm.addEventListener("click", async () => {
    if (!hasSavedParty) {
      dialog.close();
      return;
    }
    withdrawConfirm.disabled = true;
    withdrawCancel.disabled = true;
    msg.textContent = str.loading;
    try {
      await deleteDoc(rsvpRef);
      hasSavedParty = false;
      prefillRsvpForm(root, [], guestLimit);
      setWithdrawVisible(false);
      setSubmitLabel();
      msg.textContent = str.rsvpWithdrawnToast;
      dialog.close();
      try {
        const base = `${window.location.pathname}${window.location.search}`;
        history.replaceState(null, "", base);
      } catch {
        /* ignore */
      }
    } catch (e) {
      msg.textContent = e instanceof Error ? e.message : str.rsvpSubmitError;
    } finally {
      withdrawConfirm.disabled = false;
      withdrawCancel.disabled = false;
    }
  });
}

function renderInvite(
  root: HTMLElement,
  slug: string,
  wedding: WeddingDoc,
  msg: Messages,
  intlLocale: string,
): void {
  root.classList.remove("wedy-root--landing");
  document.title = inviteDocumentTitle(wedding, msg);
  teardownInviteHeroLayoutSync();
  destroyVenueMap();
  const heroSrc = wedding.backgroundUrl ?? "";
  const scheduleLines = formatWeddingScheduleLines(wedding, intlLocale, {
    dateTBD: msg.dateTBD,
    scheduleStarts: msg.scheduleStarts,
    scheduleEnds: msg.scheduleEnds,
  });
  const mealKeys = getActiveMealOptionKeys(wedding);
  const hostMealNote = typeof wedding.hostMealNote === "string" ? wedding.hostMealNote : "";
  const guestLimit =
    typeof wedding.guestLimit === "number" && wedding.guestLimit >= 1 ? wedding.guestLimit : null;
  const rsvpClosed = wedding.rsvpEnabled === true && isRsvpDeadlinePassed(wedding);
  const canRsvp = wedding.rsvpEnabled === true && !rsvpClosed && guestLimit !== null;

  const inviteCard = inviteHeroCardHtml(wedding, heroSrc, scheduleLines, msg);
  const venueHtml = venueSection(wedding, msg);
  const weatherHtml = buildWeatherSectionHtml(wedding, msg, intlLocale);
  const descHtml = descSection(wedding, msg);
  const hasSideColumns = venueHtml.length > 0 || descHtml.length > 0 || weatherHtml.length > 0;
  const heroRowHtml = hasSideColumns
    ? `<div class="wedy-invite-layout">
    <div class="wedy-invite-layout__hero">${inviteCard}</div>
    <div class="wedy-invite-layout__side">${venueHtml}${weatherHtml}${descHtml}</div>
  </div>`
    : inviteCard;

  const timelineHtml = timelineSection(wedding, msg, intlLocale);

  const rsvpBlock =
    wedding.rsvpEnabled !== true
      ? `<p class="wedy-subtext" style="text-align:center;">${escapeHtml(msg.rsvpNotEnabled)}</p>`
      : rsvpClosed
        ? `<p class="wedy-subtext" style="text-align:center;">${escapeHtml(msg.rsvpDeadlinePassed)}</p>`
        : guestLimit === null
          ? `<p class="wedy-subtext" style="text-align:center;">${escapeHtml(msg.rsvpNotReady)}</p>`
          : rsvpFormCardHtml(wedding, guestLimit, mealKeys, hostMealNote, msg, intlLocale);

  const songBlock = wedding.isSongRequestEnabled === true ? songRequestCardHtml(msg) : "";

  const main = el(`<main class="wedy-stack">
      ${heroRowHtml}
      ${timelineHtml}
      ${rsvpBlock}
      ${songBlock}
    </main>`);

  getAppContentMount(root).replaceChildren(main);
  setPageHeroBackground(root, heroSrc);
  startCountdown(main, msg);
  attachInviteLayoutHeroHeight(main);
  mountVenueMapKitWhenVisible(main, wedding);
  void hydrateWeatherIfPresent(main, msg, intlLocale);
  attachCalendarDownload(main, wedding);
  if (canRsvp && guestLimit !== null) attachRsvpForm(main, slug, guestLimit, msg);
  if (wedding.isSongRequestEnabled === true) attachSongRequestForm(main, slug, msg);
}

function timelineSection(w: WeddingDoc, msg: Messages, intlLocale: string): string {
  const items = getNormalizedTimelineItems(w);
  if (items.length === 0) return "";
  const rows = items
    .map(
      (item) => `<li class="wedy-timeline__item">
        <span class="wedy-timeline__time">${escapeHtml(formatTimelineItemTime(item.at, w, intlLocale))}</span>
        <span class="wedy-timeline__label">${escapeHtml(item.label)}</span>
      </li>`,
    )
    .join("");
  return `<div class="wedy-card">
    ${sectionCardHeadSimple(msg.timeline, WEDY_CARD_HEAD_ICON_TIMELINE)}
    <div class="wedy-card__body">
    <ol class="wedy-timeline" role="list">${rows}</ol>
    </div>
  </div>`;
}

function venueSection(w: WeddingDoc, msg: Messages): string {
  const name = (w.venueName ?? "").trim();
  const addr = (w.venueAddress ?? "").trim();
  if (!name && !addr) return "";
  const coords = getVenueLatLng(w);
  const map =
    coords == null
      ? ""
      : `<div class="wedy-map-stack">
          <div class="wedy-map-wrap">
            <div
              class="wedy-map-frame wedy-mapkit-host"
              data-wedy-mapkit
              role="img"
              aria-label="${escapeHtml(msg.venueMapAria)}"
            ></div>
          </div>
          ${mapDirectionsRowHtml(coords.lat, coords.lng, msg)}
        </div>`;
  const venueCardClass = coords == null ? "wedy-card" : "wedy-card wedy-card--venue-stack";
  return `<div class="${venueCardClass}">
    ${sectionCardHeadSimple(msg.location, WEDY_CARD_HEAD_ICON_LOCATION)}
    <div class="wedy-card__body">
    ${name ? `<p class="wedy-label" style="margin:0;">${escapeHtml(name)}</p>` : ""}
    ${addr ? `<p class="wedy-subtext">${escapeHtml(addr)}</p>` : ""}
    ${map}
    </div>
  </div>`;
}

function descSection(w: WeddingDoc, msg: Messages): string {
  const t = typeof w.description === "string" ? w.description.trim() : "";
  if (!t) return "";
  return `<div class="wedy-card">
    ${sectionCardHeadSimple(msg.about, WEDY_CARD_HEAD_ICON_ABOUT)}
    <div class="wedy-card__body">
    <p class="wedy-label" style="margin:0;font-weight:400;white-space:pre-line;">${escapeHtml(t)}</p>
    </div>
  </div>`;
}

function getInvitePageUrl(): string {
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

function attachCalendarDownload(main: HTMLElement, wedding: WeddingDoc): void {
  const btn = main.querySelector("#wedy-calendar-download");
  if (!(btn instanceof HTMLButtonElement)) return;
  btn.addEventListener("click", () => {
    const body = buildWeddingIcsString(wedding, getInvitePageUrl());
    if (!body) return;
    downloadTextFileAsBlob(
      calendarInviteIcsFilename(wedding.slug, wedding.eventTitle),
      body,
      "text/calendar;charset=utf-8",
    );
  });
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

async function main(): Promise<void> {
  const root = document.querySelector("#app");
  if (!(root instanceof HTMLElement)) return;

  const appLocale = resolveAppLocale();
  const uiMessages = getMessages(appLocale);
  const intlLocale = getIntlLocale(appLocale);
  const landingCopy = getLandingStrings(appLocale);

  const legalKind = getLegalPageFromPath();
  if (legalKind) {
    applyLegalDocumentLocale(appLocale, landingCopy, legalKind);
    renderLegalPage(root, appLocale, landingCopy, legalKind);
    return;
  }

  const slug = getSlugFromPath();
  if (!slug) {
    applyLandingDocumentLocale(appLocale, landingCopy);
    renderLanding(root, appLocale, landingCopy);
    return;
  }

  applyDocumentLocale(appLocale, uiMessages);

  renderLoading(root, uiMessages);
  try {
    const wedding = await loadWeddingBySlug(slug);
    if (!wedding) {
      applyLandingDocumentLocale(appLocale, landingCopy);
      renderLanding(root, appLocale, landingCopy);
      return;
    }
    renderInvite(root, wedding.slug, wedding, uiMessages, intlLocale);
  } catch (e) {
    const errText = e instanceof Error ? e.message : uiMessages.unknownError;
    renderError(root, errText, uiMessages);
  }
}

void main();
